import { sameAddress } from '../../../shared/routeAnchor';
import type { MeetingEntry, RouteAnchor } from '../../../shared/routeAnchor';
import { reasonText, reasonTone } from './meetingPoint';
import type { FormStatus, MeetingDraft } from './meetingPoint';

/**
 * The Route tab's two-hop meeting-point states (CAY-115, D122 amendment): what the search box and
 * the typed form say between the pick (hop 1, the queued PUT) and the Census acknowledgement (hop 2).
 * One pure function decides both, so the search screen and the stops screen never disagree.
 */

/** How long "Finding the spot…" holds after a save: the client's budget for the PUT that runs Census (D120). */
export const PICK_STATUS_MS = 20_000;

/** The last pending address this device sent, with the name the rep picked ('' for a typed entry).
 *  Memory only (module-level so a tab switch keeps it): D120/D122 never store Google's name. */
export interface PendingPick { entry: MeetingEntry; name: string; startedAt: number }
let memory: PendingPick | null = null;

/** Records a pending save. A typed re-submit of the picked address keeps the picked name. */
export const rememberPick = (entry: MeetingEntry, name: string | undefined, now: number): void => {
  const kept = name ?? (memory && sameAddress(memory.entry, entry) ? memory.name : '');
  memory = { entry, name: kept, startedAt: now };
};

/** The connection came back: the sync sends the queued PUT now, so the budget starts now. */
export const restartPick = (now: number): void => {
  if (memory) memory = { ...memory, startedAt: now };
};

/** The pick for this anchor's address, or null; a resolved anchor or another address lets it go. */
export const pickFor = (anchor: RouteAnchor | undefined): PendingPick | null => {
  const entry = anchor?.kind === 'unresolved' ? anchor.entry : undefined;
  if (memory && !(entry && sameAddress(memory.entry, entry))) memory = null;
  return memory;
};

/** Test seam: module state must not leak between cases. */
export const forgetPick = (): void => {
  memory = null;
};

/** A Route-search pick the form must finish: the draft plus what the box mirrors under it. */
export interface PickDraft extends MeetingDraft { name: string; missing: 'street' | 'city_zip' }

export interface BoxStatus { value: string | null; status: string; tone?: 'warn' }
export interface TwoHopView {
  box: BoxStatus;
  form: FormStatus;
  /** When the online budget runs out (ms epoch), so the caller can redraw; null when nothing is timed. */
  expiresAt: number | null;
}

const FINDING = 'Finding the spot…';
const COULD_NOT_PLACE = new Set(['no_match', 'needs_review']);
const TERMINAL = new Set(['no_match', 'needs_review', 'timeout', 'unavailable', 'bad_response']);

const draftView = (draft: PickDraft): TwoHopView => ({
  box: {
    value: draft.name,
    status: draft.missing === 'street' ? `No building number for ${draft.name} — finish the street below.` : 'Add the city and ZIP below.',
  },
  form: { line: draft.message, lockFor: null },
  expiresAt: null,
});

const pendingView = (entry: MeetingEntry, pick: PendingPick | null, online: boolean, now: number): TwoHopView => {
  const name = pick?.name || null;
  if (!online) return { box: { value: name, status: '' }, form: { line: reasonText('pending'), lockFor: null }, expiresAt: null };
  const expiresAt = pick ? pick.startedAt + PICK_STATUS_MS : null;
  if (expiresAt !== null && now < expiresAt)
    return { box: { value: name, status: name ? FINDING : '' }, form: { line: `Finding the spot for ${entry.street}…`, lockFor: entry }, expiresAt };
  // Budget spent, or an attempt this mount never saw start (e.g. after a reload): no stale "Finding".
  return {
    box: { value: name, status: '' },
    form: { line: `Still looking for ${entry.street}. Check the address or wait for the sync.`, lockFor: entry },
    expiresAt: null,
  };
};

export const twoHopState = (anchor: RouteAnchor | undefined, pick: PendingPick | null, online: boolean, now: number, draft?: PickDraft): TwoHopView => {
  if (draft) return draftView(draft);
  if (anchor?.kind !== 'unresolved') return { box: { value: null, status: '' }, form: { line: null, lockFor: null }, expiresAt: null };
  if (anchor.reason === 'pending' && anchor.entry) return pendingView(anchor.entry, pick, online, now);
  const form: FormStatus = { line: reasonText(anchor.reason), tone: reasonTone(anchor.reason), lockFor: null };
  const name = pick && TERMINAL.has(anchor.reason) ? pick.name || null : null;
  // "Couldn't place" blames the address, so only the outcomes that mean it; a timeout keeps the name quietly.
  const placeFailed = name && anchor.entry && COULD_NOT_PLACE.has(anchor.reason);
  return {
    box: placeFailed ? { value: name, status: `Couldn't place ${anchor.entry!.street} — check the address below.`, tone: 'warn' } : { value: name, status: '' },
    form,
    expiresAt: null,
  };
};
